import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';
import { formatAmount } from './tdhpTemplate';

/**
 * Builds rows for PDF export with formatted amounts and row type for styling.
 * Same structure as income statement PDF - modern styling with zebra striping, colored totals.
 * @param {Object} templateData - TDHP balance sheet data
 * @returns {Array<{label: string, amountStr: string, type: string, isAltLine?: boolean}>}
 */
function buildPdfRowsWithType(templateData) {
  const rows = [];

  if (!templateData?.sections) {
    return rows;
  }

  let lineRowCount = 0;
  templateData.sections.forEach((section) => {
    const sectionSubtotal = section.blocks.reduce((acc, block) => acc + (block.blockSubtotal || 0), 0);
    rows.push({ label: section.title, amountStr: formatAmount(sectionSubtotal), type: ROW_SECTION, isAltLine: false });

    section.blocks.forEach((block) => {
      rows.push({
        label: `${block.blockCode} ${block.blockTitle}`,
        amountStr: formatAmount(block.blockSubtotal ?? 0),
        type: ROW_BLOCK,
        isAltLine: false
      });

      block.groups.forEach((group) => {
        rows.push({
          label: group.groupTitle,
          amountStr: formatAmount(group.subtotal ?? 0),
          type: ROW_GROUP,
          isAltLine: false
        });

        group.lines.forEach((line) => {
          if (line.hidden) return;
          const label = line.contra ? `${line.code} ${line.label} (-)` : `${line.code} ${line.label}`;
          const isAltLine = lineRowCount++ % 2 === 1;
          rows.push({ label, amountStr: formatAmount(line.amount ?? 0), type: ROW_LINE, isAltLine });
        });
      });
    });
  });

  const totals = templateData.totals || {};
  rows.push({ label: 'TOPLAM VARLIKLAR', amountStr: formatAmount(totals.assetsTotal ?? 0), type: ROW_TOTAL, isAltLine: false });
  rows.push({ label: 'TOPLAM KAYNAKLAR', amountStr: formatAmount(totals.liabilitiesEquityTotal ?? 0), type: ROW_TOTAL, isAltLine: false });
  rows.push({ label: 'FARK', amountStr: formatAmount(totals.diff ?? 0), type: ROW_TOTAL, isAltLine: false });

  return rows;
}

/** Row types for styling */
const ROW_SECTION = 'section';
const ROW_BLOCK = 'block';
const ROW_GROUP = 'group';
const ROW_LINE = 'line';
const ROW_TOTAL = 'total';

/**
 * Builds rows for Excel export with raw numeric values and row type for styling.
 * @param {Object} templateData - TDHP balance sheet data
 * @returns {Array<{label: string, amount: number, type: string}>}
 */
function buildExcelRows(templateData) {
  const rows = [];

  if (!templateData?.sections) {
    return rows;
  }

  const toNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

  templateData.sections.forEach((section) => {
    const sectionSubtotal = section.blocks.reduce((acc, block) => acc + (block.blockSubtotal || 0), 0);
    rows.push({ label: section.title, amount: toNum(sectionSubtotal), type: ROW_SECTION });

    section.blocks.forEach((block) => {
      rows.push({
        label: `${block.blockCode} ${block.blockTitle}`,
        amount: toNum(block.blockSubtotal),
        type: ROW_BLOCK
      });

      block.groups.forEach((group) => {
        rows.push({ label: group.groupTitle, amount: toNum(group.subtotal), type: ROW_GROUP });

        group.lines.forEach((line) => {
          if (line.hidden) return;
          const label = line.contra ? `${line.code} ${line.label} (-)` : `${line.code} ${line.label}`;
          rows.push({ label, amount: toNum(line.amount), type: ROW_LINE });
        });
      });
    });
  });

  const totals = templateData.totals || {};
  rows.push({ label: 'TOPLAM VARLIKLAR', amount: toNum(totals.assetsTotal), type: ROW_TOTAL });
  rows.push({ label: 'TOPLAM KAYNAKLAR', amount: toNum(totals.liabilitiesEquityTotal), type: ROW_TOTAL });
  rows.push({ label: 'FARK', amount: toNum(totals.diff), type: ROW_TOTAL });

  return rows;
}

/** Modern color palette */
const COLORS = {
  headerBg: 'FF2D3748',
  headerFg: 'FFFFFFFF',
  sectionBg: 'FF4A5568',
  sectionFg: 'FFFFFFFF',
  blockBg: 'FF718096',
  blockFg: 'FFFFFFFF',
  groupBg: 'FFE2E8F0',
  groupFg: 'FF2D3748',
  lineBg: 'FFFFFFFF',
  lineAltBg: 'FFF7FAFC',
  lineFg: 'FF2D3748',
  totalBg: 'FF3182CE',
  totalFg: 'FFFFFFFF',
  border: 'FFCBD5E0'
};

/**
 * Export balance sheet to Excel (.xlsx) with modern formatting.
 * Numbers are raw values (no TL suffix), suitable for formulas and calculations.
 * @param {Object} templateData - TDHP balance sheet data
 * @param {string} filename - Output filename (without extension)
 */
export async function exportToExcel(templateData, filename = 'bilanco') {
  const rows = buildExcelRows(templateData);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Bilanco', {
    views: [{ rightToLeft: false }],
    properties: { defaultRowHeight: 20 }
  });

  ws.columns = [
    { width: 55 },
    { width: 20 }
  ];

  const applyCellStyle = (cell, font, fill, alignment) => {
    if (font) cell.font = font;
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    if (alignment) cell.alignment = alignment;
    cell.border = {
      top: { style: 'thin', color: { argb: COLORS.border } },
      left: { style: 'thin', color: { argb: COLORS.border } },
      bottom: { style: 'thin', color: { argb: COLORS.border } },
      right: { style: 'thin', color: { argb: COLORS.border } }
    };
  };

  // Header row - only columns A and B
  const headerRow = ws.addRow(['Hesap / Açıklama', 'Tutar']);
  headerRow.height = 28;
  applyCellStyle(headerRow.getCell(1), { bold: true, size: 11, color: { argb: COLORS.headerFg } }, COLORS.headerBg, { horizontal: 'left', vertical: 'middle' });
  applyCellStyle(headerRow.getCell(2), { bold: true, size: 11, color: { argb: COLORS.headerFg } }, COLORS.headerBg, { horizontal: 'right', vertical: 'middle' });

  let lineIndex = 0;
  rows.forEach((r) => {
    const row = ws.addRow([r.label, r.amount]);
    const c1 = row.getCell(1);
    const c2 = row.getCell(2);
    c2.numFmt = '#,##0.00';
    c2.alignment = { horizontal: 'right', vertical: 'middle' };
    c1.alignment = { vertical: 'middle' };

    const isAltLine = r.type === ROW_LINE && (lineIndex++ % 2 === 1);
    let font, fill;

    switch (r.type) {
      case ROW_SECTION:
        font = { bold: true, size: 10, color: { argb: COLORS.sectionFg } };
        fill = COLORS.sectionBg;
        break;
      case ROW_BLOCK:
        font = { bold: true, size: 10, color: { argb: COLORS.blockFg } };
        fill = COLORS.blockBg;
        break;
      case ROW_GROUP:
        font = { bold: true, size: 9, color: { argb: COLORS.groupFg } };
        fill = COLORS.groupBg;
        break;
      case ROW_LINE:
        font = { size: 9, color: { argb: COLORS.lineFg } };
        fill = isAltLine ? COLORS.lineAltBg : null;
        break;
      case ROW_TOTAL:
        font = { bold: true, size: 10, color: { argb: COLORS.totalFg } };
        fill = COLORS.totalBg;
        break;
      default:
        break;
    }

    applyCellStyle(c1, font, fill, null);
    applyCellStyle(c2, font, fill, null);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

/** PDF color palette - same as income statement (RGB for jspdf-autotable) */
const PDF_COLORS = {
  headerBg: [45, 55, 72],
  headerFg: [255, 255, 255],
  sectionBg: [74, 85, 104],
  sectionFg: [255, 255, 255],
  blockBg: [113, 128, 150],
  blockFg: [255, 255, 255],
  groupBg: [226, 232, 240],
  groupFg: [45, 55, 72],
  totalBg: [49, 130, 206],
  totalFg: [255, 255, 255],
  lineAltBg: [247, 250, 252],
  lineFg: [45, 55, 72],
  lineBorder: [203, 213, 224]
};

/**
 * Export balance sheet to PDF with modern styling (same as income statement).
 * Uses DejaVu Sans font for correct Turkish character rendering.
 * @param {Object} templateData - TDHP balance sheet data
 * @param {string} filename - Output filename (without extension)
 * @param {string} [headerText] - Optional header (e.g. company + period)
 */
export async function exportToPdf(templateData, filename = 'bilanco', headerText = '') {
  const dataRows = buildPdfRowsWithType(templateData);
  const body = dataRows.map((r) => [r.label, r.amountStr]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const margin = 14;

  if (headerText) {
    doc.setFontSize(10);
    doc.setTextColor(45, 55, 72);
    doc.text(headerText, margin, 12);
  }

  autoTable(doc, {
    head: [['Hesap / Açıklama', 'Tutar (TL)']],
    body,
    startY: headerText ? 18 : 14,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, font: TURKISH_FONT, textColor: PDF_COLORS.lineFg, lineWidth: 0.1, lineColor: PDF_COLORS.lineBorder },
    headStyles: {
      font: TURKISH_FONT,
      fillColor: PDF_COLORS.headerBg,
      textColor: PDF_COLORS.headerFg,
      fontStyle: 'bold',
      halign: 'left'
    },
    columnStyles: {
      0: { cellWidth: 130, halign: 'left' },
      1: { cellWidth: 45, halign: 'right' }
    },
    theme: 'plain',
    didParseCell: (data) => {
      if (data.section === 'head') {
        data.cell.styles.fillColor = PDF_COLORS.headerBg;
        data.cell.styles.textColor = PDF_COLORS.headerFg;
        data.cell.styles.fontStyle = 'bold';
        if (data.column.index === 1) data.cell.styles.halign = 'right';
      }
      if (data.section === 'body') {
        const rowData = dataRows[data.row.index];
        const { type, isAltLine } = rowData;
        if (type === ROW_SECTION) {
          data.cell.styles.fillColor = PDF_COLORS.sectionBg;
          data.cell.styles.textColor = PDF_COLORS.sectionFg;
          data.cell.styles.fontStyle = 'bold';
        } else if (type === ROW_BLOCK) {
          data.cell.styles.fillColor = PDF_COLORS.blockBg;
          data.cell.styles.textColor = PDF_COLORS.blockFg;
          data.cell.styles.fontStyle = 'bold';
        } else if (type === ROW_GROUP) {
          data.cell.styles.fillColor = PDF_COLORS.groupBg;
          data.cell.styles.textColor = PDF_COLORS.groupFg;
          data.cell.styles.fontStyle = 'bold';
        } else if (type === ROW_TOTAL) {
          data.cell.styles.fillColor = PDF_COLORS.totalBg;
          data.cell.styles.textColor = PDF_COLORS.totalFg;
          data.cell.styles.fontStyle = 'bold';
        } else {
          if (isAltLine) data.cell.styles.fillColor = PDF_COLORS.lineAltBg;
          data.cell.styles.textColor = PDF_COLORS.lineFg;
        }
      }
    }
  });

  doc.save(`${filename}.pdf`);
}
